import React, {
    FC,
    MutableRefObject,
    ReactElement,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import JSONPretty from 'react-json-pretty'
import useIsMountedRef from 'use-is-mounted-ref'
import { Skeleton } from '@material-ui/lab'
import { SelectOptions } from '../../../../React/Common/Components/Selects'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { AdminActionsLanguage, GenericAdminFilterLanguage, AdminControlLanguage } from '../../../../../i18n'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { OutlinedTextInput } from '../../Atoms/Inputs/OutlinedTextInput'
import { ON_CHANGE } from '../../../../React/Common/Constants/HookFormEventNames'
import { FormProvider, useForm, UseFormMethods } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import FormRow from '../../../../React/Common/Components/FormRow'
import { OutlinedSelect } from '../../Atoms/Inputs/OutlinedSelect'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { ANY_VALUE_NUMBER_NUMERIC, NONE_VALUE_STRING_DEFAULT } from '../../../../NonReact/common/DefaultValues'
import { ActionField, AdminAction, AdminControlFields } from './Types'
import {
    adminControlRequestAsync,
    AdminControlResponse,
    applyCompliancePolicy,
    deleteCompliancePolicy,
    deleteTenant,
    getAllUsersCompliancePolicyAsync,
    getTenant,
    getTenantUsers,
    getUserCompliancePolicyByUserAsync,
    getVoiceApps,
    queryPresenceStatusAsync
} from '../../../../React/Common/API/AdminControlClient'
import { displayError, displayInfoMessage } from '../../../../React/Common/Utils/Messages'
import { ActionIDs, getActions } from './Actions'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'
import { SelectOption } from '../../../../React/Common/Components/Selects/Types'
import { ACTION_FIELD_NAME } from './FieldNames'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { getDevices } from '../../../../NonReact/common/API/EntityAPIClient'
import { DevicesResponse } from '../../../../NonReact/Types/Types'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { DeviceFields } from '../../../../NonReact/constants/DeviceConstants'
import {
    ComplianceRecordingApplication,
    PolicyAssignment,
    PresenceStatus,
    TenantCompliancePolicy,
    UserCompliancePoliciesWrapper,
    UserCompliancePolicy,
    UsersCompliancePoliciesWrapper
} from '../../../../React/Common/Types/Types'
import { OutlineTextAreaMulti } from '../../Atoms/Inputs/OutlineTextAreaMulti'
import { StringUtils } from '../../../../NonReact/utils/StringUtils'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../NonReact/constants/React/Icon/Constants'

export const AdminControl: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(AdminActionsLanguage.NAME)
    language.loadBundle(AdminControlLanguage.NAME)

    const [filters, setFilters]: ReactState<Nullable<AdminFilterValues>> = useState<Nullable<AdminFilterValues>>()
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(false)
    const [actionResult, setActionResult]: ReactState<Definable<ReactElement>> = useState<Definable<ReactElement>>()
    const [availableDevices, setAvailableDevices]: ReactState<Definable<SelectOptions<string>>> = useState<
        Definable<SelectOptions<string>>
    >([])
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const actionResultRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const methods: UseFormMethods<AdminControlFields> = useForm<AdminControlFields>({
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: true
    })

    const {
        watch,
        handleSubmit,
        reset,
        trigger,
        setValue,
        formState: { isValid }
    }: UseFormMethods<AdminControlFields> = methods
    const selectedAction: AdminAction = watch(ACTION_FIELD_NAME)

    const actions: Partial<Record<AdminAction, ActionField[]>> = useMemo(
        (): Partial<Record<AdminAction, ActionField[]>> =>
            getActions(language, filters?.telephonyServer?.telSysType as TelSysTypes),
        [filters?.telephonyServer]
    )
    const actionOptions: SelectOptions<string> = useMemo(
        (): SelectOptions<string> =>
            Object.keys(actions).map(
                (action: string): SelectOption<string> => ({
                    value: action,
                    label: language.get(`Action_Label_${action}`)
                })
            ),
        [actions]
    )

    const createInput = useCallback(
        ({ type, id, label, options, required }: ActionField): ReactElement => {
            if (type === 'string' || type === 'password') {
                return (
                    <OutlinedTextInput
                        type={type === 'password' ? 'password' : 'text'}
                        inputName={id}
                        disabled={isFormItemDisabled}
                        labelLeft={label}
                        {...(required ? { rules: { required } } : {})}
                    />
                )
            }

            if (type === 'list') {
                return <OutlinedSelect name={id} label={label} options={options ?? []} disabled={isFormItemDisabled} />
            }

            if (type === 'boolean') {
                return <RouterCheckbox labelLeft={label} name={id} disabled={isFormItemDisabled} />
            }

            if (type === 'device') {
                return (
                    <OutlinedSelect
                        label={label}
                        name={id}
                        options={[
                            { label: NONE_VALUE_STRING_DEFAULT, value: NONE_VALUE_STRING_DEFAULT },
                            ...(availableDevices ?? [])
                        ]}
                        defaultValue={NONE_VALUE_STRING_DEFAULT}
                        disabled={isFormItemDisabled}
                        {...(required ? { rules: { required } } : {})}
                    />
                )
            }

            if (type === 'textarea') {
                return (
                    <OutlineTextAreaMulti
                        label={label}
                        name={id}
                        disabled={isFormItemDisabled}
                        {...(required ? { rules: { required } } : {})}
                    />
                )
            }

            return <></>
        },
        [JSON.stringify(availableDevices)]
    )

    const actionOutputs: Definable<ReactElement[]> = useMemo(
        (): Definable<ReactElement[]> =>
            actions?.[selectedAction]?.map(
                (field: ActionField): ReactElement => <FormRow key={field.id}>{createInput(field)}</FormRow>
            ),
        [actions, selectedAction, JSON.stringify(availableDevices), createInput]
    )

    const isPartitionNotSet: boolean =
        IsVarEmpty(filters?.partition?.value) || filters?.partition?.value === ANY_VALUE_NUMBER_NUMERIC
    const isFormItemDisabled: boolean =
        (isPartitionNotSet && filters?.telephonyServer?.telSysType !== TelSysTypes.MS_TEAMS) || isLoading

    const tenantPolicyMapper = (tenantCompliancePolicies: TenantCompliancePolicy[]): ReactElement => (
        <div className='ac-policy-result'>
            <h3 className='ac-policy-title'>
                {language.get(AdminControlLanguage.AdminControl_RecordingPolicyDetails)}
            </h3>
            {tenantCompliancePolicies.map(
                (tenantCompliancePolicy: TenantCompliancePolicy, index: number): ReactElement => (
                    <div
                        className='ac-policy-tenant-entry'
                        key={`tenant-entry-${tenantCompliancePolicy.type}-${index}`}
                    >
                        {Object.entries(tenantCompliancePolicy)
                            .filter(([, value]: [string, Nullable<unknown>]): boolean => isVarNotEmpty(value))
                            .map(
                                ([key, value]: [
                                    string,
                                    Nullable<string | boolean | number | ComplianceRecordingApplication[]>
                                ]): ReactElement => (
                                    <p key={key}>
                                        {StringUtils.capitalizeFirstLetter(key)}: {JSON.stringify(value)}
                                    </p>
                                )
                            )}
                    </div>
                )
            )}
        </div>
    )

    const policyResultMapper = (userCompliancePolicies: UserCompliancePolicy[]): ReactElement => (
        <>
            <h3 className='ac-policy-title'>{language.get(AdminControlLanguage.AdminControl_PolicyApplied)}</h3>
            {userCompliancePolicies?.map(
                ({ policyType, policyName }: UserCompliancePolicy): ReactElement => (
                    <div className='ac-policy-entry' key={policyType}>
                        <p>
                            {language.get(AdminControlLanguage.AdminControl_DisplayName)} {policyType}
                        </p>
                        {isStringNotEmpty(policyName) ? (
                            <>
                                <hr className='ac-policy-underline' />
                                <p>{policyName}</p>
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                )
            )}
        </>
    )

    const userPoliciesResultMapper = (
        usersCompliancePolicies: Record<string, UserCompliancePolicy[]>
    ): ReactElement => (
        <>
            <h3 className='ac-policy-title'>{language.get(AdminControlLanguage.AdminControl_PolicyApplied)}</h3>
            <table className='ac-policy-table' cellSpacing='0'>
                <thead>
                    <tr>
                        <th className='acpt-col'>
                            {language.get(AdminControlLanguage.AdminControl_UserPrincipalName)}
                        </th>
                        <th className='acpt-col'>
                            {language.get(AdminControlLanguage.AdminControl_TeamsComplianceRecordingPolicy)}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(usersCompliancePolicies).map(
                        ([userPrincipalName, policies]: [string, UserCompliancePolicy[]]): ReactElement => (
                            <tr key={userPrincipalName}>
                                <td className='acpt-col'>{userPrincipalName}</td>
                                <td className='acpt-col'>
                                    {policies
                                        .map(
                                            ({ policyType, policyName }: UserCompliancePolicy): string =>
                                                `${policyType} ${policyName}`
                                        )
                                        .join(', ')}
                                </td>
                            </tr>
                        )
                    )}
                </tbody>
            </table>
        </>
    )

    const userPolicyMapper = (
        compliancePoliciesContent: ReactElement,
        tenantCompliancePolicyContent: ReactElement
    ): ReactElement => (
        <div className='ac-policy-result'>
            {compliancePoliciesContent}
            {tenantCompliancePolicyContent}
        </div>
    )

    const presenceStatusMapper = (presenceStatus: PresenceStatus): ReactElement => (
        <div className='ac-policy-result'>
            <h3 className='ac-policy-title'>{language.get(AdminControlLanguage.AdminControl_PresenceStatus)}</h3>
            {Object.entries(presenceStatus)
                .filter(([, value]: [string, Nullable<unknown>]): boolean => isVarNotEmpty(value))
                .map(
                    ([key, value]: [string, Nullable<string | boolean | string[]>]): ReactElement => (
                        <p key={`presence-status-${key}`}>
                            {StringUtils.capitalizeFirstLetter(key)}: {JSON.stringify(value)}
                        </p>
                    )
                )}
        </div>
    )

    const successResponse = (): void => {
        if (isMounted.current) {
            setActionResult(<>{language.get(AdminControlLanguage.AdminControl_ResponseSuccess)}</>)
        }
    }

    const finalUnloading = (): void => {
        if (isMounted.current) {
            setIsLoading(false)
        }
    }

    const executeAction = ({ action, ...data }: AdminControlFields): void => {
        setIsLoading(true)

        if (filters?.telephonyServer?.telSysType === TelSysTypes.MS_TEAMS) {
            const telsys: number = filters?.telephonyServer?.value as number
            const partition: number = filters?.partition?.value as number
            const deviceNumber: string = data[ActionIDs.Device]
            const idOnTelSys: Nullable<string> =
                availableDevices?.find((device: SelectOption<string>): boolean => device.value === deviceNumber)
                    ?.IDOnTelSys ?? EMPTY_STRING

            if (action === AdminAction.GetTenant) {
                getTenant(telsys)
                    .then((tenantRes: Nullable<unknown>): void => setActionResult(<JSONPretty json={tenantRes} />))
                    .catch(displayError)
                    .finally(finalUnloading)
            } else if (action === AdminAction.DeleteTenant) {
                deleteTenant(telsys)
                    .then((): void =>
                        setActionResult(<>{language.get(AdminControlLanguage.AdminControl_ResponseSuccess)}</>)
                    )
                    .catch(displayError)
                    .finally(finalUnloading)
            } else if (action === AdminAction.GetUsers) {
                getTenantUsers(telsys)
                    .then((usersRes: Nullable<unknown>): void => setActionResult(<JSONPretty json={usersRes} />))
                    .catch(displayError)
                    .finally(finalUnloading)
            } else if (action === AdminAction.GetVoiceApps) {
                getVoiceApps(telsys)
                    .then((voiceApps: Nullable<unknown>): void => setActionResult(<JSONPretty json={voiceApps} />))
                    .catch(displayError)
                    .finally(finalUnloading)
            } else if (
                action === AdminAction.GetUserCompliancePolicy &&
                isStringNotEmpty(deviceNumber) &&
                deviceNumber !== NONE_VALUE_STRING_DEFAULT &&
                isStringNotEmpty(idOnTelSys)
            ) {
                getUserCompliancePolicyByUserAsync(telsys, partition, idOnTelSys)
                    .then((policies: Nullable<UserCompliancePoliciesWrapper>): void => {
                        if (
                            isVarNotEmpty(policies?.userCompliancePolicies) &&
                            isVarNotEmpty(policies?.tenantCompliancePolicies) &&
                            isMounted.current
                        ) {
                            setActionResult(
                                userPolicyMapper(
                                    policyResultMapper(policies.userCompliancePolicies),
                                    tenantPolicyMapper(policies.tenantCompliancePolicies)
                                )
                            )
                        }
                    })
                    .catch(displayError)
                    .finally(finalUnloading)
            } else if (action === AdminAction.GetUserCompliancePolicy) {
                getAllUsersCompliancePolicyAsync(telsys, partition)
                    .then((policies: Nullable<UsersCompliancePoliciesWrapper>): void => {
                        if (
                            isVarNotEmpty(policies?.usersCompliancePolicies) &&
                            isVarNotEmpty(policies?.tenantCompliancePolicies) &&
                            isMounted.current
                        ) {
                            setActionResult(
                                userPolicyMapper(
                                    userPoliciesResultMapper(policies.usersCompliancePolicies),
                                    tenantPolicyMapper(policies.tenantCompliancePolicies)
                                )
                            )
                        }
                    })
                    .catch(displayError)
                    .finally(finalUnloading)
            } else if (
                (action === AdminAction.ApplyAkixiCapturePolicy || action === AdminAction.DeleteUserPolicy) &&
                isStringNotEmpty(data[ActionIDs.Policies])
            ) {
                const policyAssignments: PolicyAssignment[] = JSON.parse(data[ActionIDs.Policies]) as PolicyAssignment[]

                if (action === AdminAction.DeleteUserPolicy) {
                    deleteCompliancePolicy(telsys, partition, idOnTelSys, policyAssignments)
                        .then(successResponse)
                        .catch(displayError)
                        .finally(finalUnloading)
                } else {
                    applyCompliancePolicy(telsys, partition, idOnTelSys, policyAssignments)
                        .then(successResponse)
                        .catch(displayError)
                        .finally(finalUnloading)
                }
            } else if (action === AdminAction.QueryPresenceStatus) {
                queryPresenceStatusAsync(telsys, partition, idOnTelSys)
                    .then((presenceStatus: Nullable<PresenceStatus>): void => {
                        if (isVarNotEmpty(presenceStatus) && isMounted.current) {
                            setActionResult(presenceStatusMapper(presenceStatus))
                        }
                    })
                    .catch(displayError)
                    .finally(finalUnloading)
            }
        } else {
            adminControlRequestAsync(filters?.telephonyServer?.value as number, filters?.partition?.value as number, {
                action,
                ...data
            })
                .then((result: Nullable<AdminControlResponse>): void => {
                    if (!IsVarEmpty(result) && isMounted.current) {
                        setActionResult(
                            <>
                                {isStringNotEmpty(result.message)
                                    ? result.message
                                    : language.get(AdminControlLanguage.AdminControl_ResponseSuccess)}
                            </>
                        )
                    }
                })
                .catch(displayError)
                .finally((): void => {
                    if (isMounted.current) setIsLoading(false)
                })
        }
    }

    const renderActionResult = (): ReactElement => {
        if (isLoading) {
            return (
                <div className='admin-controls-wrapper'>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                </div>
            )
        }

        return actionResult ? (
            <div className='admin-controls-wrapper' ref={actionResultRef}>
                <div className='acw-copy-wrapper'>
                    <IconButton
                        iconName={IconName.COPY}
                        type={IconButtonType.BUTTON}
                        onClick={(): void => {
                            navigator.clipboard
                                .writeText(actionResultRef.current?.textContent ?? EMPTY_STRING)
                                .then((): void => {
                                    displayInfoMessage(
                                        language.get(AdminControlLanguage.AdminControl_CopiedToClipboard)
                                    )
                                })
                        }}
                    />
                </div>
                {actionResult}
            </div>
        ) : (
            <></>
        )
    }

    useEffect(() => {
        if (!ArrayUtils.isEmpty(actionOptions)) {
            setValue(ACTION_FIELD_NAME, actionOptions?.[0]?.value)
        }
    }, [JSON.stringify(actionOptions)])

    useEffect((): void => {
        setValue(ActionIDs.Device, NONE_VALUE_STRING_DEFAULT)

        if (
            isVarNotEmpty(filters?.telephonyServer?.value) &&
            filters?.telephonyServer?.value !== ANY_VALUE_NUMBER_NUMERIC &&
            isVarNotEmpty(filters?.partition?.value) &&
            filters?.partition?.value !== ANY_VALUE_NUMBER_NUMERIC
        ) {
            getDevices(filters?.telephonyServer.value, filters?.partition?.value, false, undefined, undefined, 10000, [
                DeviceFields.ID_ON_TEL_SYS
            ])
                .then((devices: DevicesResponse[]): void => {
                    if (isMounted.current) {
                        setAvailableDevices(
                            devices
                                .filter(({ IDOnTelSys }: DevicesResponse): boolean => isStringNotEmpty(IDOnTelSys))
                                .map(
                                    ({
                                        IDOnTelSys,
                                        Description,
                                        DeviceNumber
                                    }: DevicesResponse): SelectOption<string> => ({
                                        label: Description,
                                        value: DeviceNumber,
                                        IDOnTelSys: IDOnTelSys ?? EMPTY_STRING
                                    })
                                )
                        )
                    }
                })
                .catch(displayError)
        }
    }, [filters])

    return (
        <div className='admin-controls-container'>
            <PageTitle title={AdminActionsLanguage.Page_Title} />
            <div className='admin-controls-wrapper'>
                <AdminFilters
                    labelTelSysPartitionAllSentinelAsNone
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: false,
                        search: false,
                        includeRefresh: false
                    }}
                    localeText={{
                        cmeLabel: language.get(AdminActionsLanguage.Field_Label_CME),
                        telephonyServerLabel: language.get(AdminActionsLanguage.Field_Label_TelephonyServer),
                        partitionLabel: language.get(AdminActionsLanguage.Field_Label_Partition)
                    }}
                    onChange={(values: AdminFilterValues): void => setFilters(values)}
                    alignGrid={false}
                    labelAlign='left'
                />
                <FormProvider {...methods}>
                    <form
                        className='admin-controls-form'
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onSubmit={handleSubmit(executeAction)}
                        noValidate
                    >
                        <FormRow>
                            <OutlinedSelect
                                name={ACTION_FIELD_NAME}
                                label={language.get(AdminActionsLanguage.Action_Label)}
                                options={actionOptions}
                                disabled={isFormItemDisabled}
                                handleChange={(): void => {
                                    trigger('')
                                }}
                            />
                        </FormRow>
                        {actionOutputs}
                        <div className='admin-controls-action-buttons'>
                            <RoundedButton
                                type='button'
                                disabled={isFormItemDisabled || !isValid}
                                onClick={(): void => reset()}
                                isOutlined={true}
                                shrink
                            >
                                {language.get(AdminActionsLanguage.Action_Button_Label_Reset)}
                            </RoundedButton>
                            <RoundedButton type='submit' disabled={isFormItemDisabled || !isValid} shrink>
                                {language.get(AdminActionsLanguage.Action_Button_Label_Execute)}
                            </RoundedButton>
                        </div>
                    </form>
                </FormProvider>
            </div>
            {renderActionResult()}
        </div>
    )
}
